<script setup lang="ts">
import { computed, ref } from 'vue';
import { useTemplateRefsList } from '@vueuse/core';
import { useLoanAppProcess } from '@/helpers/useLoanAppProcess';
import { PlusCircleIcon } from '@heroicons/vue/20/solid';
import { ApplicantType, AppValues } from '@/helpers/const';
import { createApplicant } from '@/helpers/builder';
import type { ApplicantEntry } from '@/types';
import {
  useRelationshipManager,
  transformApiToUiFormat,
  transformRelationshipStatuses,
  prepareFinancials,
} from '@/helpers/useRelationshipManager';
import ApplicantCard from './modules/ApplicantDetails/ApplicantCard.vue';
import PrivacyConsentModal from '@/components/shared/PrivacyConsentModal.vue';
import LogRocket from 'logrocket';
import { useFeatureFlags } from '@/helpers/useFeatureFlags';

const { loanApp, saveAndGoNext, goBack } = useLoanAppProcess();
const { getMaxSecondaryApplicants } = useFeatureFlags();

const initializeApplicants = (): ApplicantEntry[] => {
  if (loanApp.value.applicants.length > 0) {
    return transformApiToUiFormat(loanApp.value.applicants);
  }
  return [createApplicant(ApplicantType.PRIMARY)];
};

const initialApplicants = initializeApplicants();
const primary = ref<ApplicantEntry>(
  initialApplicants.find((a) => a.type === ApplicantType.PRIMARY) ?? createApplicant(ApplicantType.PRIMARY),
);
const secondaries = ref<ApplicantEntry[]>(initialApplicants.filter((a) => a.type === ApplicantType.SECONDARY));

const primaryRef = ref<InstanceType<typeof ApplicantCard>>();
const secondaryRefs = useTemplateRefsList<InstanceType<typeof ApplicantCard>>();

const allowAdditionalApplicants = computed(() => secondaries.value.length < getMaxSecondaryApplicants());
const allApplicants = computed(() => [primary.value, ...secondaries.value]);

const { validate: relationshipValidate, handleRelationshipChange: relationshipManagerHandleChange } =
  useRelationshipManager(allApplicants);

const handleRelationshipChange = (applicantId: string, newStatus: string, oldStatus?: string) => {
  relationshipManagerHandleChange(applicantId, newStatus, oldStatus, primary, secondaries);
};

const validateApplicants = async () => {
  const validatePrimary = (await primaryRef.value?.update()) ?? false;
  const renderedRefs = secondaryRefs.value.filter((ref) => ref?.update);
  const validateSecondaries = await Promise.all(renderedRefs.map((ref) => ref.update()));
  const allSecondariesValid = validateSecondaries.every(Boolean);
  return validatePrimary && allSecondariesValid;
};

const validateRelations = () => {
  const relErrors = relationshipValidate.value;
  if (relErrors.length) {
    return false;
  }
  return true;
};

const addApplicant = async () => {
  if (await validateApplicants()) {
    secondaries.value.push(createApplicant(ApplicantType.SECONDARY));
  }
};

const removeApplicant = (applicantId: string) => {
  secondaries.value = secondaries.value.filter((a) => a.id !== applicantId);
};

const saveAndContinue = async () => {
  if (!(await validateApplicants())) return;

  if (!validateRelations()) return;

  const applicants = transformRelationshipStatuses(allApplicants.value);
  const financials = prepareFinancials(allApplicants.value, loanApp.value.financials);

  const domainUrl = import.meta.env.VITE_APP_CUSTOM_THEME_DOMAIN_URL ?? 'apply.bridgit.com.au';
  if (window.location.host === domainUrl && primary.value.email?.length) {
    LogRocket.identify(primary.value.email, {
      name: `${primary.value.firstName} ${primary.value.lastName}`,
      email: primary.value.email as string,
      mobilePhoneNumber: primary.value.mobile as string,
    });
  }

  saveAndGoNext({
    applicants,
    financials,
  });
};

const showPrivacyModal = ref(false);
</script>

<template>
  <div>
    <div class="mb-8 pb-2">
      <h2 class="text-2xl font-medium">
        Provide
        <BxHighlight>your details</BxHighlight>
      </h2>
      <p v-if="allowAdditionalApplicants" class="mt-2">
        You can add a second applicant by clicking 'add another applicant' below.
      </p>

      <p class="mt-2">
        If there are more than two applicants, we will request the information for the remaining applicants after the
        application is submitted.
      </p>
    </div>

    <div class="mt-6">
      <ApplicantCard
        ref="primaryRef"
        v-model="primary"
        type="primary"
        :all-applicants="allApplicants"
        :current-id="primary.id"
        @relationship-change="handleRelationshipChange"
      />
    </div>

    <div v-if="secondaries.length > 0" class="mt-6">
      <ApplicantCard
        v-for="(applicant, index) in secondaries"
        :key="applicant.id || index"
        :ref="secondaryRefs.set"
        v-model="secondaries[index]"
        type="secondary"
        :primary="primary"
        :all-applicants="allApplicants"
        :current-id="applicant.id"
        @remove="removeApplicant"
        @relationship-change="handleRelationshipChange"
      />
    </div>

    <div v-if="allowAdditionalApplicants" class="mt-6">
      <BxButton id="applicants-add" variant="tertiary" @click="addApplicant()">
        <PlusCircleIcon class="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
        Add another applicant
      </BxButton>
    </div>

    <p v-if="loanApp.userType !== 'Broker'" class="mt-10 text-sm">
      By clicking <strong>"Save and continue"</strong> you confirm that you have read and agree to
      {{ `${AppValues.COMPANY_NAME}'s` }}
      <a class="link" @click="showPrivacyModal = true"> Privacy Consent and Electronic Authorisation</a> and consent to
      the collection, use, holding and disclosure of my/our personal information as set out in that document, and to
      receiving notices, statements, disclosures and other documents from {{ AppValues.COMPANY_NAME }} electronically.
    </p>

    <div class="mt-10">
      <BxButton id="applicants-continue" variant="primary" @click="saveAndContinue()">Save and continue</BxButton>
      <BxButton variant="link" class="ml-4" @click="goBack()">Back</BxButton>
    </div>

    <PrivacyConsentModal v-model="showPrivacyModal" />
  </div>
</template>

<style lang="scss" scoped>
.link {
  @apply cursor-pointer text-bridgit-navy underline hover:text-bridgit-vibrantGreen;
}
</style>
